import { useMemo, useState } from 'react';
import { useQuery } from '../../hooks/useQuery';
import { api, type PlanVersion, type PlanItemRow, type CatalogLineRow, type ItemMethod } from '../../lib/api';
import { Spinner, ActionButton, Select, Modal } from '../ui';

/**
 * Planning v2 Budget Grid — the primary editing surface. Rows are the catalog
 * line hierarchy; under each leaf sit its plan items (editable). Computed lines
 * (gm, op_profit, cash) render read-only in their catalog positions. Manual item
 * cells edit directly; derived item cells prompt an override. Adding/editing an
 * item opens a centered popup (method + its params) so entry never drops to the
 * bottom of the page. Edits buffer locally and commit in one batch, then
 * recompute.
 */

const MONEY = (n: number | undefined) => (n === undefined ? '' : n.toLocaleString(undefined, { maximumFractionDigits: 0 }));

function monthsOf(startYmd: string, endYmd: string): string[] {
  const s = startYmd.slice(0, 7).split('-').map(Number);
  const e = endYmd.slice(0, 7).split('-').map(Number);
  const out: string[] = [];
  let y = s[0]!, m = s[1]!;
  while (y < e[0]! || (y === e[0]! && m <= e[1]!)) { out.push(`${y}-${String(m).padStart(2, '0')}`); m++; if (m > 12) { m = 1; y++; } }
  return out;
}

type PendingValue = { kind: 'value' | 'override'; itemId: string; month: string; amount: number | null };

export default function PlanBudgetGrid({ plan, canWrite, onChanged }: { plan: PlanVersion; canWrite: boolean; onChanged?: () => void }) {
  const planId = plan.id;
  const months = useMemo(() => monthsOf(plan.horizon_start, plan.horizon_end), [plan.horizon_start, plan.horizon_end]);
  const blend = !!plan.actuals_through;

  const catalogQ = useQuery(() => api.fpa.catalog(), [planId]);
  const itemsQ = useQuery(() => api.fpa.plans.items(planId), [planId]);
  const linesQ = useQuery(() => (blend ? api.fpa.plans.blendedLines(planId) : api.fpa.plans.lines(planId)), [planId, blend]);
  const deptsQ = useQuery(() => api.fpa.departments(), [planId]);

  const [dept, setDept] = useState<string>('__all__');
  const [pending, setPending] = useState<Record<string, PendingValue>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Popup editor: create (line/dept preset, no item) or edit (existing item).
  const [editor, setEditor] = useState<{ line: string; dept: string; item?: PlanItemRow } | null>(null);

  if (catalogQ.isLoading || itemsQ.isLoading || linesQ.isLoading) return <Spinner />;
  const catalog = catalogQ.data?.catalog ?? [];
  const items = itemsQ.data?.items ?? [];
  const lines = (linesQ.data?.lines ?? []) as Array<{ line_code: string; department_id: string | null; period_month: string; amount: number; source?: string }>;
  const departments = deptsQ.data?.departments ?? [];

  const scopeDept = dept === '__all__' ? null : dept;
  const lineAt = (code: string, m: string) => lines.find((l) => l.line_code === code && l.department_id === scopeDept && l.period_month === m)?.amount;
  const sourceAt = (code: string, m: string) => lines.find((l) => l.line_code === code && l.department_id === scopeDept && l.period_month === m)?.source;

  const byParent = new Map<string | null, CatalogLineRow[]>();
  for (const c of [...catalog].sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code))) {
    const k = c.parent_code;
    (byParent.get(k) ?? byParent.set(k, []).get(k)!).push(c);
  }
  const itemsByLine = new Map<string, PlanItemRow[]>();
  for (const it of items) {
    if (scopeDept && it.department_id !== scopeDept) continue;
    (itemsByLine.get(it.line_code) ?? itemsByLine.set(it.line_code, []).get(it.line_code)!).push(it);
  }

  const bufferKey = (kind: string, itemId: string, m: string) => `${kind}|${itemId}|${m}`;
  function displayVal(it: PlanItemRow, m: string): number | undefined {
    const pv = pending[bufferKey('value', it.id, m)] ?? pending[bufferKey('override', it.id, m)];
    if (pv) return pv.amount ?? undefined;
    if (it.overrides?.[m]) return it.overrides[m]!.amount;
    // Computed series covers every method (manual surfaces its stored values);
    // fall back to raw values for freshly-typed cells before recompute.
    if (it.computed?.[m] !== undefined) return it.computed[m];
    if (it.values?.[m] !== undefined) return it.values[m];
    return undefined;
  }
  function setCell(it: PlanItemRow, m: string, raw: string) {
    const amount = raw.trim() === '' ? null : Number(raw);
    if (amount !== null && !Number.isFinite(amount)) return;
    const kind: 'value' | 'override' = it.method === 'manual' || it.method === 'actuals_seed' ? 'value' : 'override';
    setPending((p) => ({ ...p, [bufferKey(kind, it.id, m)]: { kind, itemId: it.id, month: m, amount } }));
  }

  const dirty = Object.keys(pending).length > 0;

  async function save() {
    setBusy(true); setErr(null);
    try {
      const values = Object.values(pending).filter((p) => p.kind === 'value').map((p) => ({ item_id: p.itemId, period_month: p.month, amount: p.amount }));
      const overrides = Object.values(pending).filter((p) => p.kind === 'override').map((p) => ({ item_id: p.itemId, period_month: p.month, amount: p.amount }));
      if (values.length || overrides.length) await api.fpa.plans.itemsBatch(planId, { values, overrides });
      await api.fpa.plans.recompute(planId);
      setPending({});
      itemsQ.refetch(); linesQ.refetch(); onChanged?.();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }
  async function deleteItem(id: string) {
    if (!window.confirm('Delete this item and its values/overrides?')) return;
    setBusy(true); setErr(null);
    try { await api.fpa.plans.itemsBatch(planId, { deleteItems: [id] }); await api.fpa.plans.recompute(planId); itemsQ.refetch(); linesQ.refetch(); onChanged?.(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  const cellStyle: React.CSSProperties = { padding: '2px 6px', textAlign: 'right', fontSize: 11, fontVariantNumeric: 'tabular-nums', borderBottom: '0.5px solid var(--color-border)' };
  const labelStyle: React.CSSProperties = { padding: '3px 8px', fontSize: 11, borderBottom: '0.5px solid var(--color-border)', position: 'sticky', left: 0, background: 'var(--color-surface)', zIndex: 1 };
  // Visible affordance so it's obvious the monthly cells are editable.
  const input: React.CSSProperties = { width: 66, fontSize: 11, textAlign: 'right', border: '0.5px solid var(--color-border)', borderRadius: 3, padding: '1px 4px', background: 'var(--color-surface)', color: 'var(--color-text)', fontVariantNumeric: 'tabular-nums' };
  const editableCanWrite = canWrite && plan.status === 'draft';

  function renderLeaf(leaf: CatalogLineRow, indent: number) {
    const its = itemsByLine.get(leaf.code) ?? [];
    const rows: React.ReactNode[] = [];
    rows.push(
      <tr key={`leaf-${leaf.code}`}>
        <td style={{ ...labelStyle, paddingLeft: 8 + indent * 12, fontWeight: 500 }}>
          {leaf.label}
          {editableCanWrite && (
            <button onClick={() => setEditor({ line: leaf.code, dept })} style={{ marginLeft: 6, fontSize: 10, background: 'none', border: 'none', color: 'var(--color-blue)', cursor: 'pointer' }}>+ item</button>
          )}
        </td>
        <td style={cellStyle}>{MONEY(months.reduce((s, m) => s + (lineAt(leaf.code, m) ?? 0), 0))}</td>
        {months.map((m) => {
          const src = sourceAt(leaf.code, m);
          return <td key={m} style={{ ...cellStyle, background: src === 'actual' ? 'var(--color-surface-alt, rgba(120,120,120,0.08))' : undefined }}>{MONEY(lineAt(leaf.code, m))}</td>;
        })}
      </tr>,
    );
    for (const it of its) {
      const isOverrideRow = it.method !== 'manual' && it.method !== 'actuals_seed';
      rows.push(
        <tr key={`item-${it.id}`} style={{ opacity: it.is_active ? 1 : 0.5 }}>
          <td style={{ ...labelStyle, paddingLeft: 8 + (indent + 1) * 12, color: 'var(--color-text-muted)' }}>
            <button onClick={() => setEditor({ line: it.line_code, dept: it.department_id ?? '__all__', item: it })}
              title="Edit item" style={{ background: 'none', border: 'none', color: 'var(--color-text)', cursor: 'pointer', padding: 0, fontSize: 11, textAlign: 'left' }}>
              {it.label}
            </button>
            <span style={{ fontSize: 9, marginLeft: 4, color: 'var(--color-text-muted)' }}>{it.method}{it.sign === -1 ? ' (−)' : ''}</span>
          </td>
          <td style={cellStyle}>{MONEY(months.reduce((s, m) => s + (displayVal(it, m) ?? 0), 0))}</td>
          {months.map((m) => {
            const overridden = !!it.overrides?.[m] || !!pending[bufferKey('override', it.id, m)];
            const editable = editableCanWrite && (!blend || m > (plan.actuals_through ?? ''));
            return (
              <td key={m} style={{ ...cellStyle, background: overridden ? 'rgba(240,180,0,0.14)' : undefined }} title={overridden ? 'override' : isOverrideRow ? 'derived — type to override' : ''}>
                {editable
                  ? <input style={input} defaultValue={displayVal(it, m) ?? ''} onBlur={(e) => setCell(it, m, e.target.value)} />
                  : MONEY(displayVal(it, m))}
              </td>
            );
          })}
        </tr>,
      );
    }
    return rows;
  }

  function renderNode(code: string | null, indent: number): React.ReactNode[] {
    const children = byParent.get(code) ?? [];
    const out: React.ReactNode[] = [];
    for (const c of children) {
      if (c.kind === 'leaf') { out.push(...renderLeaf(c, indent)); continue; }
      const isComputed = c.kind === 'computed';
      out.push(
        <tr key={`grp-${c.code}`} style={{ background: 'var(--color-surface-alt, rgba(120,120,120,0.04))' }}>
          <td style={{ ...labelStyle, paddingLeft: 8 + indent * 12, fontWeight: 600, background: 'var(--color-surface-alt, rgba(120,120,120,0.04))' }}>{c.label}</td>
          <td style={{ ...cellStyle, fontWeight: 600 }}>{MONEY(months.reduce((s, m) => s + (lineAt(c.code, m) ?? 0), 0))}</td>
          {months.map((m) => <td key={m} style={{ ...cellStyle, fontWeight: 600 }}>{MONEY(lineAt(c.code, m))}</td>)}
        </tr>,
      );
      if (!isComputed) out.push(...renderNode(c.code, indent + 1));
    }
    return out;
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 12px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Department</span>
        <Select value={dept} onChange={setDept}>
          <option value="__all__">Consolidated</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </Select>
        {editableCanWrite && <ActionButton onClick={() => setEditor({ line: 'cost.fixed.rent', dept })}>+ Add item</ActionButton>}
        {blend && <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>Actuals through {plan.actuals_through} (grey = actual)</span>}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {err && <span style={{ fontSize: 11, color: 'var(--color-danger, #c0392b)' }}>{err}</span>}
          {dirty && <span style={{ fontSize: 11, color: 'var(--color-amber)' }}>Unsaved</span>}
          {editableCanWrite && <ActionButton variant="primary" onClick={save} disabled={busy || !dirty}>{busy ? 'Saving…' : 'Save & recompute'}</ActionButton>}
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...labelStyle, fontWeight: 600, textAlign: 'left', zIndex: 2 }}>Line</th>
              <th style={{ ...cellStyle, fontWeight: 600 }}>Total</th>
              {months.map((m) => <th key={m} style={{ ...cellStyle, fontWeight: 600 }}>{m.slice(2)}</th>)}
            </tr>
          </thead>
          <tbody>{renderNode(null, 0)}</tbody>
        </table>
      </div>
      {editor && (
        <ItemEditor
          planId={planId} line={editor.line} presetDept={editor.dept} item={editor.item} months={months}
          catalog={catalog} departments={departments}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); itemsQ.refetch(); linesQ.refetch(); onChanged?.(); }}
          onDelete={editor.item ? () => { const id = editor.item!.id; setEditor(null); deleteItem(id); } : undefined}
        />
      )}
    </div>
  );
}

// ─── Item editor popup (create + edit, with method-specific params) ──────────

const METHODS: ItemMethod[] = ['manual', 'spread', 'growth', 'pct_of', 'per_head', 'formula', 'actuals_seed'];

// Percentages are entered as whole numbers (10 = 10%) and stored as decimals.
function defaultParams(method: ItemMethod, line: string): Record<string, unknown> {
  switch (method) {
    case 'spread': return { total: 0, period: 'year', profile: 'even' };
    case 'growth': return { base: 0, rate: 0, cadence: 'mom' };
    case 'pct_of': return { ref: 'revenue.total', pct: 0, lag_months: 0, scope: 'same_department' };
    case 'per_head': return { amount_per_head_month: 0, headcount_filter: {} };
    case 'formula': return { expression: '0' };
    case 'actuals_seed': return { source_line: line, reference: 'last_3mo_avg', uplift_pct: 0 };
    default: return {};
  }
}

function ItemEditor({ planId, line, presetDept, item, months, catalog, departments, onClose, onSaved, onDelete }: {
  planId: string; line: string; presetDept: string; item?: PlanItemRow; months: string[];
  catalog: CatalogLineRow[]; departments: Array<{ id: string; name: string }>;
  onClose: () => void; onSaved: () => void; onDelete?: () => void;
}) {
  const leaves = catalog.filter((c) => c.kind === 'leaf' && c.is_active);
  const [lineCode, setLineCode] = useState(item?.line_code ?? line);
  const [label, setLabel] = useState(item?.label ?? '');
  const [dept, setDept] = useState(item?.department_id ?? (presetDept === '__all__' ? '__all__' : presetDept));
  const [method, setMethod] = useState<ItemMethod>(item?.method ?? 'manual');
  const [sign, setSign] = useState<1 | -1>(item?.sign ?? 1);
  const [start, setStart] = useState(item?.start_month ?? '');
  const [end, setEnd] = useState(item?.end_month ?? '');
  const [params, setParams] = useState<Record<string, unknown>>(item?.params ?? defaultParams(method, line));
  const [fill, setFill] = useState('');           // manual: fill all months with this
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function changeMethod(m: ItemMethod) { setMethod(m); setParams(defaultParams(m, lineCode)); }
  const P = (k: string, v: unknown) => setParams((p) => ({ ...p, [k]: v }));
  const num = (v: string) => (v.trim() === '' ? 0 : Number(v));
  const g = (k: string) => (params as Record<string, unknown>)[k];

  async function save() {
    if (!label.trim()) { setErr('Label is required'); return; }
    setBusy(true); setErr(null);
    try {
      const resp = await api.fpa.plans.itemsBatch(planId, {
        items: [{
          id: item?.id, line_code: lineCode, label: label.trim(),
          department_id: dept === '__all__' ? null : dept, method, params, sign,
          start_month: start || null, end_month: end || null,
          expected_updated_at: item?.updated_at,
        }],
      });
      const itemId = item?.id ?? resp.item_ids[0];
      // Manual quick-fill: write the same amount to every horizon month.
      if ((method === 'manual' || method === 'actuals_seed') && fill.trim() !== '' && itemId) {
        const amt = Number(fill);
        if (Number.isFinite(amt)) await api.fpa.plans.itemsBatch(planId, { values: months.map((m) => ({ item_id: itemId, period_month: m, amount: amt })) });
      }
      await api.fpa.plans.recompute(planId);
      onSaved();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  const box: React.CSSProperties = { fontSize: 12, padding: '4px 7px', border: '0.5px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)' };
  const row: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 };
  const lab: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-muted)', width: 110 };
  const muted: React.CSSProperties = { fontSize: 10, color: 'var(--color-text-muted)' };
  // Uncontrolled numeric input (defaultValue + onChange) so mid-typing values
  // like "0.05" or "5" are never reformatted out from under the cursor.
  const NumField = ({ k, width = 140, pct = false }: { k: string; width?: number; pct?: boolean }) => (
    <input type="number" step="any"
      defaultValue={pct ? String(Number(g(k) ?? 0) * 100) : String(Number(g(k) ?? 0))}
      onChange={(e) => P(k, pct ? num(e.target.value) / 100 : num(e.target.value))}
      style={{ ...box, width }} />
  );

  return (
    <Modal title={item ? 'Edit item' : 'Add item'} subtitle={item ? `${item.method} · ${item.line_code}` : lineCode} onClose={onClose} width={520}>
      <div style={{ padding: 20 }}>
        {err && <p style={{ color: 'var(--color-danger, #c0392b)', fontSize: 12 }}>{err}</p>}
        <div style={row}><span style={lab}>Label</span><input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. AWS hosting" style={{ ...box, flex: 1 }} /></div>
        <div style={row}>
          <span style={lab}>Line</span>
          <select value={lineCode} onChange={(e) => setLineCode(e.target.value)} style={{ ...box, flex: 1 }}>
            {leaves.map((c) => <option key={c.code} value={c.code}>{c.label} ({c.code})</option>)}
          </select>
        </div>
        <div style={row}>
          <span style={lab}>Department</span>
          <select value={dept} onChange={(e) => setDept(e.target.value)} style={{ ...box, flex: 1 }}>
            <option value="__all__">Company</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div style={row}>
          <span style={lab}>Method</span>
          <select value={method} onChange={(e) => changeMethod(e.target.value as ItemMethod)} style={{ ...box, flex: 1 }} disabled={!!item && item.method.startsWith('engine')}>
            {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        {/* Method-specific params — keyed by method so fields remount cleanly. */}
        <div key={method}>
          {method === 'manual' && (
            <div style={row}><span style={lab}>Amount / month</span><input type="number" step="any" value={fill} onChange={(e) => setFill(e.target.value)} placeholder="optional — fills every month" style={{ ...box, width: 180 }} /></div>
          )}
          {method === 'spread' && (
            <>
              <div style={row}><span style={lab}>Total</span><NumField k="total" /></div>
              <div style={row}><span style={lab}>Period</span><select value={String(g('period') ?? 'year')} onChange={(e) => P('period', e.target.value)} style={box}>{['year', 'quarter', 'horizon'].map((x) => <option key={x}>{x}</option>)}</select>
                <select value={String(g('profile') ?? 'even')} onChange={(e) => P('profile', e.target.value)} style={box}>{['even', 'seasonal', 'custom'].map((x) => <option key={x}>{x}</option>)}</select></div>
            </>
          )}
          {method === 'growth' && (
            <>
              <div style={row}><span style={lab}>Base</span><NumField k="base" /></div>
              <div style={row}><span style={lab}>Growth rate</span><NumField k="rate" width={90} pct /><span style={muted}>% per step</span></div>
              <div style={row}><span style={lab}>Cadence</span><select value={String(g('cadence') ?? 'mom')} onChange={(e) => P('cadence', e.target.value)} style={box}>{['mom', 'yoy', 'annual_step'].map((x) => <option key={x}>{x}</option>)}</select></div>
            </>
          )}
          {method === 'pct_of' && (
            <>
              <div style={row}><span style={lab}>Ref</span><input value={String(g('ref') ?? '')} onChange={(e) => P('ref', e.target.value)} placeholder="revenue.total / rev.tm / …" style={{ ...box, flex: 1 }} /></div>
              <div style={row}><span style={lab}>Percent</span><NumField k="pct" width={90} pct /><span style={muted}>%</span></div>
              <div style={row}><span style={lab}>Lag (months)</span><NumField k="lag_months" width={70} />
                <select value={String(g('scope') ?? 'same_department')} onChange={(e) => P('scope', e.target.value)} style={box}>{['same_department', 'company'].map((x) => <option key={x}>{x}</option>)}</select></div>
            </>
          )}
          {method === 'per_head' && (
            <>
              <div style={row}><span style={lab}>$ / head / mo</span><NumField k="amount_per_head_month" width={120} /></div>
              <div style={row}><span style={lab}>Billable only</span><input type="checkbox" checked={!!(g('headcount_filter') as { billable_only?: boolean } | undefined)?.billable_only} onChange={(e) => P('headcount_filter', { ...(g('headcount_filter') as object), billable_only: e.target.checked })} /></div>
            </>
          )}
          {method === 'formula' && (
            <div style={row}><span style={lab}>Expression</span><input value={String(g('expression') ?? '')} onChange={(e) => P('expression', e.target.value)} placeholder="revenue.total * 0.12" style={{ ...box, flex: 1 }} /></div>
          )}
          {method === 'actuals_seed' && (
            <>
              <div style={row}><span style={lab}>Source line</span><input value={String(g('source_line') ?? '')} onChange={(e) => P('source_line', e.target.value)} style={{ ...box, flex: 1 }} /></div>
              <div style={row}><span style={lab}>Reference</span><select value={String(g('reference') ?? 'last_3mo_avg')} onChange={(e) => P('reference', e.target.value)} style={box}>{['last_3mo_avg', 'last_month', 'same_month_py', 'fy_ytd_avg'].map((x) => <option key={x}>{x}</option>)}</select></div>
              <div style={row}><span style={lab}>Uplift</span><NumField k="uplift_pct" width={90} pct /><span style={muted}>%</span></div>
            </>
          )}
        </div>

        <div style={{ ...row, marginTop: 6 }}>
          <span style={lab}>Sign</span>
          <select value={String(sign)} onChange={(e) => setSign(Number(e.target.value) as 1 | -1)} style={box}><option value="1">+ (normal)</option><option value="-1">− (credit)</option></select>
          <span style={{ ...lab, width: 'auto', marginLeft: 8 }}>Window</span>
          <input value={start} onChange={(e) => setStart(e.target.value)} placeholder="start YYYY-MM" style={{ ...box, width: 110 }} />
          <input value={end} onChange={(e) => setEnd(e.target.value)} placeholder="end YYYY-MM" style={{ ...box, width: 110 }} />
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <ActionButton variant="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : item ? 'Save' : 'Add item'}</ActionButton>
          <ActionButton onClick={onClose}>Cancel</ActionButton>
          {onDelete && <span style={{ marginLeft: 'auto' }}><ActionButton variant="danger" onClick={onDelete}>Delete</ActionButton></span>}
        </div>
      </div>
    </Modal>
  );
}
